import { Checkbox, CheckboxProps, FormControlLabel, FormLabel, Grid } from "@mui/material";
import classNames from "classnames";
import { FieldMetaProps } from "formik";
import React, { useMemo, useState } from "react";
import { FieldError } from "./FieldError";
import { OCFormControl } from "./FormControl";

export interface ICheckboxFieldProps extends CheckboxProps {
  input?: any;
  custom?: any;
  name?: string;
  label?: string;
  labelPlacement?: "end" | "start" | "top" | "bottom";
  isDisabled?: boolean;
  disabled?: boolean;
  className?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => void;
  checked?: boolean;
  hideFocus?: boolean;
  meta?: Partial<FieldMetaProps<any>>;
  labelSX?: any;
}

// CheckboxField COMPONENT
export const CheckboxField = (props: ICheckboxFieldProps): JSX.Element => {
  const {
    label,
    name,
    value,
    onFocus,
    onBlur,
    labelPlacement,
    isDisabled,
    disabled,
    checked,
    onChange,
    hideFocus,
    meta,
    labelSX,
  } = props;
  const [isFocused, setIsFocused] = useState<boolean>(false);

  const internalValue = useMemo(() => (value === true || value === false ? !!value : !!checked), [value, checked]);

  const dirty: boolean = useMemo(() => {
    if (!meta) return false;
    const { initialValue, touched } = meta;
    return touched && internalValue !== initialValue ? true : false;
  }, [meta, internalValue]);

  const handleFocus = (e: React.FocusEvent<HTMLButtonElement>): void => {
    setIsFocused(true);
    if (onFocus && typeof onFocus === "function") {
      onFocus(e);
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLButtonElement>): void => {
    setIsFocused(false);
    if (onBlur && typeof onBlur === "function") {
      onBlur(e);
    }
  };

  const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    if (onChange && typeof onChange === "function") {
      onChange(event, !internalValue);
    }
  };

  const hasError = meta?.error ? true : false;

  return (
    <OCFormControl error={hasError} sx={{ width: "100%", my: 0.3 }}>
      <Grid container sx={{ display: "flex", alignItems: "start" }}>
        <Grid
          item
          sx={{
            display: "flex",
            justifyContent: hasError ? "space-between" : "flex-end",
            textAlign: "right",
          }}
          xs={5}
        >
          <FieldError meta={meta} icon={true} />
          {label && <FormLabel sx={{ ...labelSX }}>{label}</FormLabel>}
        </Grid>
        <Grid item xs={7}>
          <Checkbox
            id={name}
            name={name}
            checked={internalValue}
            onChange={handleOnChange}
            onBlur={handleBlur}
            onFocus={handleFocus}
            disabled={isDisabled || disabled}
            sx={{ m: 0, p: 0, mt: "3px" }}
          />
        </Grid>
      </Grid>
    </OCFormControl>
  );
};
